import './afiliacionFlow.css';
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
    Afiliacion,
    updatePasoAfiliacion,
    updateDatosOficinaVirtual,
    fetchCorrespondenciasAfiliacion,
} from '../../../services/afiliadosService';
import {
    X,
    CheckCircle2,
    Clock,
    FileText,
    Upload,
    AlertCircle,
    Building2,
    Sparkles,
    Calendar,
    FileCheck,
    Check,
    ExternalLink,
    ChevronDown,
    ChevronUp,
    Shield,
    Camera,
    Gift,
    Save,
    Lock,
    Package,
    Zap,
} from 'lucide-react';

interface GestionAfiliacionModalProps {
    afiliacion: Afiliacion;
    onClose: () => void;
    onUpdated: (afiliacion: Afiliacion) => void;
}

const GestionAfiliacionModal: React.FC<GestionAfiliacionModalProps> = ({
    afiliacion,
    onClose,
    onUpdated,
}) => {
    const [currentAfiliacion, setCurrentAfiliacion] = useState<Afiliacion>(afiliacion);
    const [activePaso, setActivePaso] = useState<number>(1);
    const [loadingPaso, setLoadingPaso] = useState<number | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [successMsg, setSuccessMsg] = useState<string | null>(null);

    // Form inputs por paso
    const [pasoArchivos, setPasoArchivos] = useState<{ [key: string]: File | null }>({});
    const [nitInput, setNitInput] = useState<string>(afiliacion.nit || '');
    const [obsRecepcion, setObsRecepcion] = useState<string>(afiliacion.paso6_observaciones || '');

    // Correspondencias recibidas para esta afiliación
    const [correspondenciasList, setCorrespondenciasList] = useState<any[]>([]);
    const [loadingCorrespondencias, setLoadingCorrespondencias] = useState(false);

    useEffect(() => {
        if (currentAfiliacion.id) {
            setLoadingCorrespondencias(true);
            fetchCorrespondenciasAfiliacion(currentAfiliacion.id)
                .then((data) => setCorrespondenciasList(data))
                .catch(() => setCorrespondenciasList([]))
                .finally(() => setLoadingCorrespondencias(false));
        }
    }, [currentAfiliacion.id]);

    const [datosEmpresa, setDatosEmpresa] = useState({empresa_nombre: afiliacion.empresa_nombre, representante_legal: afiliacion.representante_legal || '', nit: afiliacion.nit || '', telefono_contacto: afiliacion.telefono_contacto || '', correo_contacto: afiliacion.correo_contacto || '', rubro: afiliacion.rubro || ''});
    const [guardandoEmpresa, setGuardandoEmpresa] = useState(false);
    const guardarEmpresa = async () => {
        setGuardandoEmpresa(true); setError(null);
        try {
            const actualizado = await updateDatosOficinaVirtual(currentAfiliacion.id, datosEmpresa);
            setCurrentAfiliacion(actualizado); onUpdated(actualizado); setSuccessMsg('Datos de la empresa guardados.');
        } catch { setError('No se pudieron guardar los datos de la empresa.'); }
        finally { setGuardandoEmpresa(false); }
    };

    // Regla estricta de secuencialidad del diagrama de Miro
    const isStepUnlocked = (pasoNum: number): boolean => {
        if (pasoNum === 1) return true;
        if (pasoNum === 2) return currentAfiliacion.paso1_contrato_enviado;
        if (pasoNum === 3) return currentAfiliacion.paso2_contrato_firmado;
        if (pasoNum === 4) return currentAfiliacion.paso3_docs_fiscales_enviados;
        if (pasoNum === 5) return currentAfiliacion.paso4_requisitos_recibidos;
        if (pasoNum === 6) return currentAfiliacion.paso5_nit_exhibido;
        if (pasoNum === 7) return currentAfiliacion.paso6_kardex_recepcion_abierto;
        return false;
    };

    // Validar si el paso cuenta con los documentos requeridos cargados
    const hasRequiredDocument = (pasoNum: number): boolean => {
        if (pasoNum === 1) {
            return Boolean(pasoArchivos['paso1_archivo'] || currentAfiliacion.paso1_archivo);
        }
        if (pasoNum === 2) {
            return Boolean(pasoArchivos['paso2_archivo'] || currentAfiliacion.paso2_archivo);
        }
        if (pasoNum === 3) {
            return Boolean(
                pasoArchivos['paso3_factura_servicio'] ||
                currentAfiliacion.paso3_factura_servicio ||
                pasoArchivos['paso3_croquis'] ||
                currentAfiliacion.paso3_croquis
            );
        }
        if (pasoNum === 4) {
            return Boolean(
                pasoArchivos['paso4_roe'] ||
                currentAfiliacion.paso4_roe ||
                pasoArchivos['paso4_matricula'] ||
                currentAfiliacion.paso4_matricula ||
                pasoArchivos['paso4_ultima_factura'] ||
                currentAfiliacion.paso4_ultima_factura
            );
        }
        if (pasoNum === 5) {
            return Boolean(
                pasoArchivos['paso5_archivo_nit'] ||
                currentAfiliacion.paso5_archivo_nit ||
                pasoArchivos['paso5_foto_exhibicion'] ||
                currentAfiliacion.paso5_foto_exhibicion
            );
        }
        return true; // Pasos 6 y 7 no requieren archivo
    };

    const handleFileChange = (key: string, file: File | null) => {
        setPasoArchivos((prev) => ({ ...prev, [key]: file }));
    };

    const handleActualizarPaso = async (pasoNum: number, completadoValor?: boolean) => {
        if (!isStepUnlocked(pasoNum)) {
            setError(`No puedes realizar el Paso ${pasoNum} sin haber completado previamente el Paso ${pasoNum - 1} en el flujo.`);
            return;
        }

        const vaACompletar = completadoValor !== undefined ? completadoValor : true;
        if (vaACompletar && !hasRequiredDocument(pasoNum)) {
            setError(`Para marcar el Paso ${pasoNum} como completado es obligatorio adjuntar el archivo/documento de respaldo.`);
            return;
        }

        setLoadingPaso(pasoNum);
        setError(null);
        setSuccessMsg(null);

        try {
            const formData = new FormData();
            formData.append('paso', pasoNum.toString());

            if (pasoNum === 1) {
                const comp = completadoValor !== undefined ? completadoValor : !currentAfiliacion.paso1_contrato_enviado;
                formData.append('paso1_contrato_enviado', comp.toString());
                if (pasoArchivos['paso1_archivo']) {
                    formData.append('paso1_archivo', pasoArchivos['paso1_archivo'] as Blob);
                }
            } else if (pasoNum === 2) {
                const comp = completadoValor !== undefined ? completadoValor : !currentAfiliacion.paso2_contrato_firmado;
                formData.append('paso2_contrato_firmado', comp.toString());
                if (pasoArchivos['paso2_archivo']) {
                    formData.append('paso2_archivo', pasoArchivos['paso2_archivo'] as Blob);
                }
            } else if (pasoNum === 3) {
                const comp = completadoValor !== undefined ? completadoValor : !currentAfiliacion.paso3_docs_fiscales_enviados;
                formData.append('paso3_docs_fiscales_enviados', comp.toString());
                if (pasoArchivos['paso3_factura_servicio']) {
                    formData.append('paso3_factura_servicio', pasoArchivos['paso3_factura_servicio'] as Blob);
                }
                if (pasoArchivos['paso3_croquis']) {
                    formData.append('paso3_croquis', pasoArchivos['paso3_croquis'] as Blob);
                }
            } else if (pasoNum === 4) {
                const comp = completadoValor !== undefined ? completadoValor : !currentAfiliacion.paso4_requisitos_recibidos;
                formData.append('paso4_requisitos_recibidos', comp.toString());
                if (pasoArchivos['paso4_roe']) {
                    formData.append('paso4_roe', pasoArchivos['paso4_roe'] as Blob);
                }
                if (pasoArchivos['paso4_matricula']) {
                    formData.append('paso4_matricula', pasoArchivos['paso4_matricula'] as Blob);
                }
                if (pasoArchivos['paso4_ultima_factura']) {
                    formData.append('paso4_ultima_factura', pasoArchivos['paso4_ultima_factura'] as Blob);
                }
            } else if (pasoNum === 5) {
                const comp = completadoValor !== undefined ? completadoValor : !currentAfiliacion.paso5_nit_exhibido;
                formData.append('paso5_nit_exhibido', comp.toString());
                formData.append('nit', nitInput);
                if (pasoArchivos['paso5_archivo_nit']) {
                    formData.append('paso5_archivo_nit', pasoArchivos['paso5_archivo_nit'] as Blob);
                }
                if (pasoArchivos['paso5_foto_exhibicion']) {
                    formData.append('paso5_foto_exhibicion', pasoArchivos['paso5_foto_exhibicion'] as Blob);
                }
            } else if (pasoNum === 6) {
                const comp = completadoValor !== undefined ? completadoValor : !currentAfiliacion.paso6_kardex_recepcion_abierto;
                formData.append('paso6_kardex_recepcion_abierto', comp.toString());
                formData.append('paso6_observaciones', obsRecepcion);
            } else if (pasoNum === 7) {
                const comp = completadoValor !== undefined ? completadoValor : !currentAfiliacion.paso7_kardex_horas_abierto;
                formData.append('paso7_kardex_horas_abierto', comp.toString());
            }

            const res = await updatePasoAfiliacion(currentAfiliacion.id, formData);
            setCurrentAfiliacion(res);
            onUpdated(res);
            setSuccessMsg(`Paso ${pasoNum} actualizado correctamente.`);
            setTimeout(() => setSuccessMsg(null), 3000);
        } catch (err: any) {
            /* Browser diagnostics omitted. */
            setError(err.response?.data?.detail || 'Error al actualizar el paso.');
        } finally {
            setLoadingPaso(null);
        }
    };


    const pasosConfig = [
        {
            num: 1,
            titulo: '1. Envío de contrato',
            subtitulo: 'Envío formal del contrato de afiliación a la empresa/cliente',
            completado: currentAfiliacion.paso1_contrato_enviado,
            fecha: currentAfiliacion.paso1_fecha,
            fase: 'FASE 1: LEGAL Y CONTRACTUAL',
            renderContent: () => (
                <div className="space-y-4 pt-2">
                    <p className="text-xs text-gray-600">
                        Sube o verifica el archivo del contrato enviado para revisión del cliente.
                    </p>
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                        <label className="flex items-center gap-2 px-3.5 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-semibold rounded-xl cursor-pointer border border-gray-300 transition">
                            <Upload className="w-4 h-4 text-gray-500" />
                            <span>{pasoArchivos['paso1_archivo'] ? pasoArchivos['paso1_archivo']?.name : 'Adjuntar Contrato PDF'}</span>
                            <input
                                type="file"
                                accept=".pdf,.doc,.docx"
                                className="hidden"
                                onChange={(e) => handleFileChange('paso1_archivo', e.target.files?.[0] || null)}
                            />
                        </label>
                        {currentAfiliacion.paso1_archivo && (
                            <a
                                href={currentAfiliacion.paso1_archivo}
                                target="_blank"
                                rel="noreferrer"
                                className="flex items-center gap-1 text-xs text-blue-600 hover:underline font-semibold"
                            >
                                <FileText className="w-3.5 h-3.5" /> Ver Contrato Actual <ExternalLink className="w-3 h-3" />
                            </a>
                        )}
                    </div>
                </div>
            ),
        },
        {
            num: 2,
            titulo: '2. Recepción de contrato firmado',
            subtitulo: 'El cliente remite el contrato debidamente firmado',
            completado: currentAfiliacion.paso2_contrato_firmado,
            fecha: currentAfiliacion.paso2_fecha,
            fase: 'FASE 1: LEGAL Y CONTRACTUAL',
            renderContent: () => (
                <div className="space-y-4 pt-2">
                    <p className="text-xs text-gray-600">
                        Respaldo digital del contrato firmado por el representante legal.
                    </p>
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                        <label className="flex items-center gap-2 px-3.5 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-semibold rounded-xl cursor-pointer border border-gray-300 transition">
                            <Upload className="w-4 h-4 text-gray-500" />
                            <span>{pasoArchivos['paso2_archivo'] ? pasoArchivos['paso2_archivo']?.name : 'Subir Contrato Firmado (PDF)'}</span>
                            <input
                                type="file"
                                accept=".pdf,.png,.jpg,.jpeg"
                                className="hidden"
                                onChange={(e) => handleFileChange('paso2_archivo', e.target.files?.[0] || null)}
                            />
                        </label>
                        {currentAfiliacion.paso2_archivo && (
                            <a
                                href={currentAfiliacion.paso2_archivo}
                                target="_blank"
                                rel="noreferrer"
                                className="flex items-center gap-1 text-xs text-emerald-600 hover:underline font-semibold"
                            >
                                <FileCheck className="w-3.5 h-3.5" /> Ver Contrato Firmado <ExternalLink className="w-3 h-3" />
                            </a>
                        )}
                    </div>
                </div>
            ),
        },
        {
            num: 3,
            titulo: '3. Envío de documentos fiscales',
            subtitulo: 'Co Marca entrega Factura de Servicios y Croquis para el SIN',
            completado: currentAfiliacion.paso3_docs_fiscales_enviados,
            fecha: currentAfiliacion.paso3_fecha,
            fase: 'FASE 2: DOCUMENTACIÓN FISCAL (BOLIVIA)',
            renderContent: () => (
                <div className="space-y-4 pt-2">
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-amber-900 text-xs flex items-start gap-2">
                        <Shield className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                        <div>
                            <strong>Requisitos entregados por Co Marca a la empresa:</strong>
                            <ul className="list-disc pl-4 mt-1 space-y-0.5">
                                <li>Factura de agua o luz de las instalaciones de Co Marca</li>
                                <li>Croquis oficial de ubicación de Co Marca (Calacoto, La Paz)</li>
                            </ul>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                            <label className="text-[11px] font-bold text-gray-700">• Factura de Agua o Luz:</label>
                            <label className="flex items-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-semibold rounded-xl cursor-pointer border border-gray-300 transition">
                                <Upload className="w-4 h-4 text-gray-500" />
                                <span className="truncate">{pasoArchivos['paso3_factura_servicio']?.name || 'Subir Factura'}</span>
                                <input
                                    type="file"
                                    accept=".pdf,.png,.jpg,.jpeg"
                                    className="hidden"
                                    onChange={(e) => handleFileChange('paso3_factura_servicio', e.target.files?.[0] || null)}
                                />
                            </label>
                            {currentAfiliacion.paso3_factura_servicio && (
                                <a href={currentAfiliacion.paso3_factura_servicio} target="_blank" rel="noreferrer" className="text-[11px] text-blue-600 hover:underline flex items-center gap-1 font-medium">
                                    Ver Factura <ExternalLink className="w-3 h-3" />
                                </a>
                            )}
                        </div>

                        <div className="space-y-1.5">
                            <label className="text-[11px] font-bold text-gray-700">• Croquis de Co Marca:</label>
                            <label className="flex items-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-semibold rounded-xl cursor-pointer border border-gray-300 transition">
                                <Upload className="w-4 h-4 text-gray-500" />
                                <span className="truncate">{pasoArchivos['paso3_croquis']?.name || 'Subir Croquis'}</span>
                                <input
                                    type="file"
                                    accept=".pdf,.png,.jpg,.jpeg"
                                    className="hidden"
                                    onChange={(e) => handleFileChange('paso3_croquis', e.target.files?.[0] || null)}
                                />
                            </label>
                            {currentAfiliacion.paso3_croquis && (
                                <a href={currentAfiliacion.paso3_croquis} target="_blank" rel="noreferrer" className="text-[11px] text-blue-600 hover:underline flex items-center gap-1 font-medium">
                                    Ver Croquis <ExternalLink className="w-3 h-3" />
                                </a>
                            )}
                        </div>
                    </div>
                </div>
            ),
        },
        {
            num: 4,
            titulo: '4. Recepción de requisitos empresa',
            subtitulo: 'Recepción de ROE, Matrícula de Comercio y Última Factura',
            completado: currentAfiliacion.paso4_requisitos_recibidos,
            fecha: currentAfiliacion.paso4_fecha,
            fase: 'FASE 2: DOCUMENTACIÓN FISCAL (BOLIVIA)',
            renderContent: () => (
                <div className="space-y-4 pt-2">
                    <p className="text-xs text-gray-600">
                        Documentación de constitución y cumplimiento legal remitida por la empresa a Co Marca:
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div className="space-y-1.5">
                            <label className="text-[11px] font-bold text-gray-700">• ROE (Min. Trabajo):</label>
                            <label className="flex items-center gap-1.5 px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-semibold rounded-xl cursor-pointer border border-gray-300 transition">
                                <Upload className="w-3.5 h-3.5 text-gray-500" />
                                <span className="truncate">{pasoArchivos['paso4_roe']?.name || 'Adjuntar ROE'}</span>
                                <input
                                    type="file"
                                    accept=".pdf,.png,.jpg"
                                    className="hidden"
                                    onChange={(e) => handleFileChange('paso4_roe', e.target.files?.[0] || null)}
                                />
                            </label>
                            {currentAfiliacion.paso4_roe && (
                                <a href={currentAfiliacion.paso4_roe} target="_blank" rel="noreferrer" className="text-[11px] text-blue-600 hover:underline flex items-center gap-1 font-medium">
                                    Ver ROE <ExternalLink className="w-3 h-3" />
                                </a>
                            )}
                        </div>

                        <div className="space-y-1.5">
                            <label className="text-[11px] font-bold text-gray-700">• Matrícula de Comercio:</label>
                            <label className="flex items-center gap-1.5 px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-semibold rounded-xl cursor-pointer border border-gray-300 transition">
                                <Upload className="w-3.5 h-3.5 text-gray-500" />
                                <span className="truncate">{pasoArchivos['paso4_matricula']?.name || 'Adjuntar SEPREC'}</span>
                                <input
                                    type="file"
                                    accept=".pdf,.png,.jpg"
                                    className="hidden"
                                    onChange={(e) => handleFileChange('paso4_matricula', e.target.files?.[0] || null)}
                                />
                            </label>
                            {currentAfiliacion.paso4_matricula && (
                                <a href={currentAfiliacion.paso4_matricula} target="_blank" rel="noreferrer" className="text-[11px] text-blue-600 hover:underline flex items-center gap-1 font-medium">
                                    Ver Matrícula <ExternalLink className="w-3 h-3" />
                                </a>
                            )}
                        </div>

                        <div className="space-y-1.5">
                            <label className="text-[11px] font-bold text-gray-700">• Última Factura:</label>
                            <label className="flex items-center gap-1.5 px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-semibold rounded-xl cursor-pointer border border-gray-300 transition">
                                <Upload className="w-3.5 h-3.5 text-gray-500" />
                                <span className="truncate">{pasoArchivos['paso4_ultima_factura']?.name || 'Adjuntar Factura'}</span>
                                <input
                                    type="file"
                                    accept=".pdf,.png,.jpg"
                                    className="hidden"
                                    onChange={(e) => handleFileChange('paso4_ultima_factura', e.target.files?.[0] || null)}
                                />
                            </label>
                            {currentAfiliacion.paso4_ultima_factura && (
                                <a href={currentAfiliacion.paso4_ultima_factura} target="_blank" rel="noreferrer" className="text-[11px] text-blue-600 hover:underline flex items-center gap-1 font-medium">
                                    Ver Factura <ExternalLink className="w-3 h-3" />
                                </a>
                            )}
                        </div>
                    </div>
                </div>
            ),
        },
        {
            num: 5,
            titulo: '5. Solicitud y exhibición del NIT',
            subtitulo: 'Registro del NIT y exhibición física en recepción',
            completado: currentAfiliacion.paso5_nit_exhibido,
            fecha: currentAfiliacion.paso5_fecha,
            fase: 'FASE 2: DOCUMENTACIÓN FISCAL (BOLIVIA)',
            renderContent: () => (
                <div className="space-y-4 pt-2">
                    <p className="text-xs text-gray-600">
                        Registro del NIT tributario y foto de verificación de que el certificado está exhibido en la recepción de Co Marca.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="space-y-1">
                            <label className="text-[11px] font-bold text-gray-700">Número de NIT:</label>
                            <input
                                type="text"
                                value={nitInput}
                                onChange={(e) => setNitInput(e.target.value)}
                                placeholder="Ej: 1029384756"
                                className="w-full px-3 py-2 border border-gray-300 rounded-xl text-xs focus:ring-2 focus:ring-[#61257b]"
                            />
                        </div>

                        <div className="space-y-1">
                            <label className="text-[11px] font-bold text-gray-700">Certificado NIT (PDF / Imagen):</label>
                            <label className="flex items-center gap-1.5 px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-semibold rounded-xl cursor-pointer border border-gray-300 transition">
                                <Upload className="w-3.5 h-3.5 text-gray-500" />
                                <span className="truncate">{pasoArchivos['paso5_archivo_nit']?.name || 'Subir Certificado NIT'}</span>
                                <input
                                    type="file"
                                    accept=".pdf,.png,.jpg"
                                    className="hidden"
                                    onChange={(e) => handleFileChange('paso5_archivo_nit', e.target.files?.[0] || null)}
                                />
                            </label>
                            {currentAfiliacion.paso5_archivo_nit && (
                                <a href={currentAfiliacion.paso5_archivo_nit} target="_blank" rel="noreferrer" className="text-[11px] text-blue-600 hover:underline flex items-center gap-1 font-medium">
                                    Ver Certificado NIT <ExternalLink className="w-3 h-3" />
                                </a>
                            )}
                        </div>

                        <div className="sm:col-span-2 space-y-1">
                            <label className="text-[11px] font-bold text-gray-700">Foto de Exhibición en Recepción (Evidencia):</label>
                            <label className="flex items-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-semibold rounded-xl cursor-pointer border border-gray-300 transition">
                                <Camera className="w-4 h-4 text-gray-500" />
                                <span className="truncate">{pasoArchivos['paso5_foto_exhibicion']?.name || 'Subir Foto en Recepción'}</span>
                                <input
                                    type="file"
                                    accept="image/*"
                                    className="hidden"
                                    onChange={(e) => handleFileChange('paso5_foto_exhibicion', e.target.files?.[0] || null)}
                                />
                            </label>
                            {currentAfiliacion.paso5_foto_exhibicion && (
                                <a href={currentAfiliacion.paso5_foto_exhibicion} target="_blank" rel="noreferrer" className="text-[11px] text-purple-600 hover:underline flex items-center gap-1 font-medium">
                                    Ver Foto de Exhibición <ExternalLink className="w-3 h-3" />
                                </a>
                            )}
                        </div>
                    </div>
                </div>
            ),
        },
        {
            num: 6,
            titulo: '6. Apertura de Kardex de recepción',
            subtitulo: 'Habilitación de recepción de correspondencia, encomiendas y visitas',
            completado: currentAfiliacion.paso6_kardex_recepcion_abierto,
            fecha: currentAfiliacion.paso6_fecha,
            fase: 'FASE 3: OPERACIONES EN SISTEMA',
            renderContent: () => (
                <div className="space-y-4 pt-2">
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-xl text-blue-900 text-xs flex items-start gap-2">
                        <Building2 className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                        <div>
                            <strong>Servicios de Recepción habilitados para el afiliado:</strong>
                            <p className="mt-0.5 text-blue-800">
                                Al marcar este paso, el personal de recepción tiene autorizado recibir paquetes, cartas, facturas y notificaciones a nombre de <strong>{currentAfiliacion.empresa_nombre}</strong> con seguimiento QR y PIN.
                            </p>
                        </div>
                    </div>

                    <div className="space-y-1">
                        <label className="text-[11px] font-bold text-gray-700">Observaciones o Instrucciones Especiales para Recepción:</label>
                        <textarea
                            rows={2}
                            value={obsRecepcion}
                            onChange={(e) => setObsRecepcion(e.target.value)}
                            placeholder="Ej: Recibir correspondencia de lunes a viernes, notificar al WhatsApp corporativo..."
                            className="w-full px-3 py-2 border border-gray-300 rounded-xl text-xs focus:ring-2 focus:ring-[#61257b]"
                        />
                    </div>

                    {/* Historial de correspondencias asociadas */}
                    <div className="p-3.5 bg-gray-50 border border-gray-200 rounded-2xl space-y-2">
                        <div className="flex items-center justify-between text-xs font-bold text-gray-800">
                            <span className="flex items-center gap-1.5">
                                <Package className="w-4 h-4 text-[#61257b]" />
                                Encomiendas y Correspondencia Recibidas ({correspondenciasList.length})
                            </span>
                        </div>

                        {loadingCorrespondencias ? (
                            <p className="text-center py-2 text-xs text-gray-400">Cargando correspondencias...</p>
                        ) : correspondenciasList.length > 0 ? (
                            <div className="max-h-40 overflow-y-auto space-y-1.5 custom-scroll pr-1">
                                {correspondenciasList.map((corr) => (
                                    <div key={corr.id} className="p-2.5 bg-white border border-gray-200 rounded-xl text-xs flex items-center justify-between shadow-sm">
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <span className="font-mono font-bold text-[#61257b]">{corr.codigo || `#${corr.id}`}</span>
                                                <span className="px-1.5 py-0.5 bg-gray-100 text-gray-700 font-semibold rounded text-[10px] uppercase">{corr.estado}</span>
                                            </div>
                                            <p className="text-[11px] text-gray-600 mt-0.5">
                                                De: <span className="font-medium text-gray-800">{corr.remitente_nombre} {corr.remitente_apellido}</span> • Para: <span className="font-medium text-gray-800">{corr.destinatario_nombre || 'Titular'}</span>
                                            </p>
                                        </div>
                                        <div className="text-right">
                                            <span className="text-[10px] text-gray-400 block font-mono">PIN: {corr.pin_seguridad || 'N/A'}</span>
                                            <span className="text-[10px] text-gray-500">{corr.fecha_recepcion ? new Date(corr.fecha_recepcion).toLocaleDateString() : ''}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-xs text-gray-500 italic py-1">No se han recibido paquetes o cartas aún para esta empresa domiciliada.</p>
                        )}
                    </div>
                </div>
            ),
        },
        {
            num: 7,
            titulo: '7. Apertura de Kardex Uso de Horas Gratuitas',
            subtitulo: 'Verificación de las horas habilitadas por la configuración del servicio adquirido',
            completado: currentAfiliacion.paso7_kardex_horas_abierto,
            fecha: currentAfiliacion.paso7_fecha,
            fase: 'FASE 3: OPERACIONES EN SISTEMA',
            renderContent: () => (
                <div className="space-y-4 pt-2">
                    <div className="p-4 bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-2xl">
                        <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                                <Gift className="w-5 h-5 text-purple-600" />
                                <div>
                                    <h5 className="text-xs font-black text-purple-950 uppercase tracking-wider">
                                        Horas de regalo configuradas
                                    </h5>
                                    <p className="text-[11px] text-purple-700">
                                        Vigencia: <strong>{currentAfiliacion.periodo_inicio} a {currentAfiliacion.periodo_fin}</strong>
                                    </p>
                                </div>
                            </div>
                            <span className="px-2.5 py-1 bg-purple-600 text-white font-mono font-bold text-xs rounded-full shadow-sm">
                                {currentAfiliacion.horas_gratuitas_disponibles}h Disponibles en el período
                            </span>
                        </div>

                        {/* Vinculación nativa con Módulo de Horas de Regalo */}
                        <div className="p-2.5 bg-white/90 rounded-xl border border-purple-200 mb-3 flex items-center justify-between text-xs">
                            <div className="flex items-center gap-2">
                                <Zap className="w-4 h-4 text-purple-600" />
                                <div>
                                    <span className="font-bold text-purple-950 block">Vinculado al Módulo de Horas de Regalo</span>
                                    <span className="text-[10px] text-purple-700">
                                        {currentAfiliacion.uso_horas_regalo_actual ? (
                                            <>Registro activo en Horas de Regalo: {currentAfiliacion.uso_horas_regalo_actual.config_horas_display || 'Horas de regalo'}</>
                                        ) : (
                                            <>Se aprovisiona automáticamente al confirmar el Paso 7</>
                                        )}
                                    </span>
                                </div>
                            </div>
                            <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 font-bold text-[10px] rounded-full border border-emerald-300">
                                Sincronizado
                            </span>
                        </div>

                        <p className="text-xs text-purple-900 leading-relaxed">
                            Las <strong>horas de regalo configuradas</strong> están disponibles durante el período vigente y <strong>NO son acumulables</strong>. Vencen al finalizar ese período.
                        </p>

                        {currentAfiliacion.ciclo_actual_info && (
                            <div className="mt-3 p-2.5 bg-white/80 rounded-xl border border-purple-200 flex items-center justify-between text-xs">
                                <div>
                                    <span className="font-bold text-purple-900">Período activo (#{currentAfiliacion.ciclo_actual_info.numero_mes}):</span>
                                    <span className="text-purple-700 ml-1.5 font-mono">
                                        {currentAfiliacion.ciclo_actual_info.fecha_inicio} al {currentAfiliacion.ciclo_actual_info.fecha_fin}
                                    </span>
                                </div>
                                <div className="font-bold text-purple-900">
                                    Consumidas: <span className="text-amber-600 font-mono">{currentAfiliacion.ciclo_actual_info.horas_consumidas}h</span> / {currentAfiliacion.horas_gratuitas_asignadas}h
                                </div>
                            </div>
                        )}

                        <div className="grid grid-cols-3 gap-2 text-center mt-3 pt-3 border-t border-purple-200">
                            <div>
                                <span className="text-[10px] text-gray-500 uppercase font-bold">Por período</span>
                                <div className="text-base font-black text-gray-800">{currentAfiliacion.horas_gratuitas_asignadas ?? 0} hrs</div>
                            </div>
                            <div>
                                <span className="text-[10px] text-gray-500 uppercase font-bold">Consumidas en el período</span>
                                <div className="text-base font-black text-amber-600">
                                    {currentAfiliacion.ciclo_actual_info ? currentAfiliacion.ciclo_actual_info.horas_consumidas : currentAfiliacion.horas_gratuitas_consumidas} hrs
                                </div>
                            </div>
                            <div>
                                <span className="text-[10px] text-gray-500 uppercase font-bold">Saldo Vigente</span>
                                <div className="text-base font-black text-emerald-600 font-mono">
                                    {currentAfiliacion.horas_gratuitas_disponibles} hrs
                                </div>
                            </div>
                        </div>

                        {/* Listado de ciclos si es contrato anual */}
                        {currentAfiliacion.ciclos_horas && currentAfiliacion.ciclos_horas.length > 1 && (
                            <div className="mt-4 pt-3 border-t border-purple-200">
                                <div className="text-[11px] font-black uppercase text-purple-900 mb-2">Períodos de horas de regalo (No Acumulables)</div>
                                <div className="max-h-36 overflow-y-auto space-y-1 pr-1 custom-scroll">
                                    {currentAfiliacion.ciclos_horas.map((c) => (
                                        <div
                                            key={c.id}
                                            className={`p-1.5 px-3 rounded-lg text-[11px] flex items-center justify-between ${
                                                c.es_activo ? 'bg-purple-100 text-purple-900 font-bold border border-purple-300' : 'bg-white/60 text-gray-600'
                                            }`}
                                        >
                                            <div className="flex items-center gap-2">
                                                <span>Período {c.numero_mes}:</span>
                                                <span className="font-mono text-[10px] text-gray-500">{c.fecha_inicio} a {c.fecha_fin}</span>
                                                {c.es_activo && <span className="text-[9px] bg-purple-600 text-white px-1.5 py-0.2 rounded-full font-bold uppercase">Período Actual</span>}
                                            </div>
                                            <div>
                                                {c.horas_disponibles}h disp. / {currentAfiliacion.horas_gratuitas_asignadas}h
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            ),
        },
    ];

    return createPortal(
        <div className="aff-flow-overlay">
            <div className="aff-flow-modal" role="dialog" aria-modal="true" aria-labelledby="aff-flow-title">
                {/* Header fijo */}
                <div className="aff-flow-header">
                    <div className="flex items-center gap-3">
                        <div className="aff-flow-avatar">
                            <Building2 className="w-6 h-6 text-white" />
                        </div>
                        <div>
                            <div className="flex items-center gap-2">
                                <h3 id="aff-flow-title">{currentAfiliacion.empresa_nombre}</h3>
                                <span className={`px-2 py-0.5 text-[10px] font-bold uppercase rounded-full tracking-wider ${
                                    currentAfiliacion.estado === 'activo'
                                        ? 'aff-flow-badge-active'
                                        : 'aff-flow-badge-pending'
                                }`}>
                                    {currentAfiliacion.estado === 'activo' ? 'Activo / Afiliado' : 'En Proceso'}
                                </span>
                            </div>
                            <p className="aff-flow-client">
                                Cliente: {currentAfiliacion.cliente_nombre} {currentAfiliacion.nit ? `• NIT: ${currentAfiliacion.nit}` : ''}
                            </p>
                        </div>
                    </div>

                    <button
                        onClick={onClose}
                        className="aff-flow-close" aria-label="Cerrar gestión de afiliación"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="aff-flow-scroll">
                    <section className="aff-company-card" aria-labelledby="aff-company-title">
                        <div className="aff-company-heading">
                            <span className="aff-company-icon"><Building2 size={21} /></span>
                            <div><span className="aff-company-eyebrow">Oficina virtual</span><h4 id="aff-company-title">Datos de la empresa</h4><p>Información fiscal y de contacto del cliente.</p></div>
                        </div>
                        <div className="aff-company-grid">
                            {([{key:'empresa_nombre',label:'Razón social',placeholder:'Nombre de la empresa',section:'Información fiscal'},
                               {key:'representante_legal',label:'Representante legal',placeholder:'Nombre completo',section:''},
                               {key:'nit',label:'NIT',placeholder:'Número de identificación tributaria',section:''},
                               {key:'rubro',label:'Rubro',placeholder:'Actividad de la empresa',section:''},
                               {key:'telefono_contacto',label:'Teléfono',placeholder:'Número de contacto',section:'Datos de contacto'},
                               {key:'correo_contacto',label:'Correo',placeholder:'correo@empresa.com',section:''}] as const).map(campo => <React.Fragment key={campo.key}>
                                {campo.section && <div className="aff-company-section">{campo.section}</div>}
                                <label htmlFor={`aff-company-${campo.key}`}><span>{campo.label}{campo.key === 'empresa_nombre' && <span className="aff-field-required"> *</span>}</span>
                                    <input id={`aff-company-${campo.key}`} type={campo.key === 'correo_contacto' ? 'email' : campo.key === 'telefono_contacto' ? 'tel' : 'text'}
                                        placeholder={campo.placeholder} value={datosEmpresa[campo.key]} disabled={guardandoEmpresa}
                                        onChange={e => setDatosEmpresa({...datosEmpresa, [campo.key]:e.target.value})} />
                                </label>
                            </React.Fragment>)}
                        </div>
                        <div className="aff-company-actions"><span>Los datos se guardan en esta afiliación.</span>
                            <button type="button" className="aff-company-save" disabled={guardandoEmpresa || !datosEmpresa.empresa_nombre.trim()} onClick={guardarEmpresa}>
                                {guardandoEmpresa ? <span className="aff-flow-spinner" aria-hidden="true" /> : <Save size={17} />}
                                {guardandoEmpresa ? 'Guardando…' : 'Guardar datos de empresa'}
                            </button>
                        </div>
                    </section>
                {/* Progress bar banner */}
                <div className="aff-flow-progress">
                    <div className="flex items-center justify-between text-xs font-bold text-gray-700 mb-1.5">
                        <span className="flex items-center gap-1.5">
                            <Sparkles className="w-3.5 h-3.5 text-[#c1348b]" />
                            Progreso del Diagrama de Afiliación
                        </span>
                        <span className="font-mono text-gray-900 font-black">
                            {currentAfiliacion.pasos_completados_count}/7 Pasos ({currentAfiliacion.porcentaje_progreso}%)
                        </span>
                    </div>
                    <div className="w-full h-2.5 bg-gray-200 rounded-full overflow-hidden">
                        <div
                            className="h-full bg-gradient-to-r from-[#c1348b] via-[#61257b] to-[#8b3fa8] rounded-full transition-all duration-500"
                            style={{ width: `${currentAfiliacion.porcentaje_progreso}%` }}
                        />
                    </div>
                </div>

                {/* Alertas */}
                {error && (
                    <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-xs flex items-center gap-2">
                        <AlertCircle className="w-4 h-4 flex-shrink-0" />
                        <span>{error}</span>
                    </div>
                )}

                {successMsg && (
                    <div className="mx-6 mt-4 p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-700 text-xs flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                        <span>{successMsg}</span>
                    </div>
                )}

                {/* Contenido scrolleable fluido */}
                <div className="aff-flow-steps space-y-4">
                    {pasosConfig.map((paso) => {
                        const isOpen = activePaso === paso.num;
                        const isCompleted = paso.completado;
                        const isUnlocked = isStepUnlocked(paso.num);
                        const isDocMissing = !isCompleted && !hasRequiredDocument(paso.num);

                        return (
                            <div
                                key={paso.num}
                                className={`border rounded-2xl transition-all duration-200 overflow-hidden ${
                                    isCompleted
                                        ? 'border-emerald-200 bg-emerald-50/20'
                                        : !isUnlocked
                                        ? 'border-gray-200 bg-gray-50/60 opacity-80'
                                        : isOpen
                                        ? 'border-[#61257b]/50 shadow-md bg-white ring-1 ring-[#61257b]/20'
                                        : 'border-gray-200 bg-white hover:border-gray-300'
                                }`}
                            >
                                {/* Header del paso */}
                                <div
                                    onClick={() => setActivePaso(isOpen ? 0 : paso.num)}
                                    className="p-4 flex items-center justify-between cursor-pointer select-none"
                                >
                                    <div className="flex items-center gap-3">
                                        <div
                                            className={`w-8 h-8 rounded-xl flex items-center justify-center font-bold text-xs transition ${
                                                isCompleted
                                                    ? 'bg-emerald-600 text-white shadow-sm'
                                                    : !isUnlocked
                                                    ? 'bg-gray-200 text-gray-500 border border-gray-300'
                                                    : 'bg-[#61257b] text-white shadow-sm animate-pulse'
                                            }`}
                                        >
                                            {isCompleted ? (
                                                <Check className="w-4 h-4" />
                                            ) : !isUnlocked ? (
                                                <Lock className="w-3.5 h-3.5" />
                                            ) : (
                                                paso.num
                                            )}
                                        </div>
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <h4 className="text-sm font-bold text-gray-900">{paso.titulo}</h4>
                                                <span className="text-[10px] text-gray-400 font-medium uppercase tracking-wider">
                                                    {paso.fase}
                                                </span>
                                            </div>
                                            <p className="text-xs text-gray-500">{paso.subtitulo}</p>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-3">
                                        {isCompleted ? (
                                            <span className="px-2.5 py-1 bg-emerald-100 text-emerald-800 text-[11px] font-bold rounded-full flex items-center gap-1">
                                                <CheckCircle2 className="w-3.5 h-3.5" /> Completado
                                            </span>
                                        ) : !isUnlocked ? (
                                            <span className="px-2.5 py-1 bg-gray-100 text-gray-500 text-[11px] font-bold rounded-full flex items-center gap-1 border border-gray-200">
                                                <Lock className="w-3 h-3" /> Bloqueado
                                            </span>
                                        ) : (
                                            <span className="px-2.5 py-1 bg-blue-100 text-blue-800 text-[11px] font-bold rounded-full flex items-center gap-1">
                                                <Clock className="w-3.5 h-3.5" /> Siguiente Paso
                                            </span>
                                        )}

                                        {isOpen ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                                    </div>
                                </div>

                                {/* Cuerpo interactivo del paso */}
                                {isOpen && (
                                    <div className="px-5 pb-5 pt-1 border-t border-gray-100 bg-white/50">
                                        {!isUnlocked && (
                                            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-xl text-amber-900 text-xs flex items-center gap-2 font-medium">
                                                <Lock className="w-4 h-4 text-amber-600 flex-shrink-0" />
                                                <span>
                                                    <strong>Paso Bloqueado:</strong> Debes completar primero el <strong>Paso {paso.num - 1}</strong> en la secuencia del diagrama de Miro para habilitar este hito.
                                                </span>
                                            </div>
                                        )}

                                        <div className={!isUnlocked ? 'pointer-events-none opacity-60' : ''}>
                                            {paso.renderContent()}
                                        </div>

                                        <div className="mt-4 pt-3 border-t border-gray-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                            <div className="flex flex-wrap items-center gap-2">
                                                <span className="text-[11px] text-gray-400">
                                                    {paso.fecha
                                                        ? `Última actualización: ${new Date(paso.fecha).toLocaleString()}`
                                                        : 'Aún no completado'}
                                                </span>
                                                {isUnlocked && isDocMissing && (
                                                    <span className="text-[11px] text-amber-700 bg-amber-50 px-2 py-0.5 rounded-md border border-amber-200 font-bold flex items-center gap-1">
                                                        <AlertCircle className="w-3.5 h-3.5 text-amber-600" />
                                                        Archivo obligatorio para completar
                                                    </span>
                                                )}
                                            </div>

                                            <button
                                                type="button"
                                                disabled={!isUnlocked || isDocMissing || loadingPaso === paso.num}
                                                onClick={() => handleActualizarPaso(paso.num)}
                                                className={`px-4 py-2 text-xs font-bold rounded-xl transition flex items-center gap-1.5 shadow-sm ${
                                                    !isUnlocked
                                                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200'
                                                        : isDocMissing
                                                        ? 'bg-amber-50 text-amber-700 border border-amber-300 cursor-not-allowed'
                                                        : isCompleted
                                                        ? 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                                                        : 'bg-[#61257b] hover:bg-[#006ca8] text-white'
                                                }`}
                                            >
                                                {loadingPaso === paso.num ? (
                                                    <Clock className="w-3.5 h-3.5 animate-spin" />
                                                ) : !isUnlocked ? (
                                                    <Lock className="w-3.5 h-3.5 text-gray-400" />
                                                ) : isDocMissing ? (
                                                    <Upload className="w-3.5 h-3.5 text-amber-600" />
                                                ) : isCompleted ? (
                                                    <Save className="w-3.5 h-3.5" />
                                                ) : (
                                                    <Check className="w-3.5 h-3.5" />
                                                )}
                                                <span>
                                                    {!isUnlocked
                                                        ? `Bloqueado (Paso ${paso.num - 1} requerido)`
                                                        : isDocMissing
                                                        ? 'Cargar Archivo para Habilitar'
                                                        : isCompleted
                                                        ? 'Guardar Cambios / Archivos'
                                                        : 'Marcar como Completado'}
                                                </span>
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                </div>

                {/* Footer fijo */}
                <div className="aff-flow-footer">
                    <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-gray-600">Saldo Horas Gratuitas:</span>
                        <span className="px-2.5 py-0.5 bg-purple-100 text-purple-800 text-xs font-black rounded-lg">
                            {currentAfiliacion.horas_gratuitas_disponibles} hrs disponibles
                        </span>
                    </div>

                    <button
                        onClick={onClose}
                        className="aff-flow-done"
                    >
                        Listo, Cerrar
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default GestionAfiliacionModal;

